import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type { CallContact, CallContactInput } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import AssetField from "./AssetField";
import TimezoneSelect from "./TimezoneSelect";

/**
 * Add or edit ONE family contact for ONE resident (VC-11).
 *
 * Three things here are load-bearing rather than cosmetic:
 *
 *  1. **Consent gates the two direction flags, in the UI, structurally.** Not a
 *     warning next to an enabled checkbox - the checkboxes are disabled and the
 *     submitted payload carries `false` until a consent record exists. A family
 *     contact that reaches a TV without a recorded basis is the finding an
 *     inspection writes down, and "staff were told to tick the box" is not a
 *     control. Saving without consent is still allowed: the row is kept as an
 *     inactive draft so the daughter's number does not have to be re-typed
 *     later, which is exactly what makes staff willing to record consent
 *     properly instead of routing around us.
 *
 *  2. **Consent-by-proxy is first-class.** Swedish senior living means a
 *     meaningful share of residents have a god man or förvaltare, and "the
 *     resident consented" is then simply false. `capacity` is a required choice
 *     the moment consent is recorded, so the record can never be ambiguous
 *     about *whose* consent it was.
 *
 *  3. **The consent timestamp is never re-stamped by an unrelated edit.**
 *     `consent` is only sent when the operator actually touched it; fixing a
 *     typo in a phone number must not move "granted at" to today, because that
 *     date is the answer to "when was this authorised".
 *
 * Scope and kind are not offered: this dialog only ever writes
 * `scope: "person" / kind: "family"`. A family row keyed to anything but the
 * resident would be inherited by the next occupant of that bed.
 */

/** What staff actually type. Free text is still allowed - this is a shortcut. */
const RELATION_SUGGESTIONS = [
  "dotter",
  "son",
  "make",
  "maka",
  "sambo",
  "syskon",
  "barnbarn",
  "god man",
  "förvaltare",
  "vän",
];

/**
 * On whose authority. Deliberately four coarse buckets rather than free text:
 * "who may consent for this person" is a legal question with a small answer
 * set, and a free-text field turns it into an unqueryable prose column.
 */
export const CONSENT_CAPACITIES: { value: string; label: string }[] = [
  { value: "resident", label: "The resident themselves" },
  { value: "god man", label: "God man (court-appointed deputy)" },
  { value: "förvaltare", label: "Förvaltare (court-appointed administrator)" },
  { value: "anhörig", label: "Anhörig / närstående (next of kin)" },
];

/** E.164: leading +, no leading zero, 7-15 digits total. */
const E164 = /^\+[1-9]\d{6,14}$/;

export function isValidE164(v: string): boolean {
  return E164.test(v.trim());
}

/**
 * Deliberately loose: "has an @, something either side, no spaces". The address
 * is not a security boundary here - the portal only ever mails a single-use
 * link to it - so a strict RFC pattern would reject real addresses for nothing.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(v: string): boolean {
  return EMAIL.test(v.trim());
}

interface Props {
  open: boolean;
  premiseId: string;
  /** The resident this contact belongs to (PhiRecord id). */
  personRef: string;
  /** Human label for the room, for the dialog subtitle. */
  roomLabel: string;
  /** Null = create. */
  contact: CallContact | null;
  /** Premise timezone, prefilled into quiet hours so it's rarely retyped. */
  defaultTz: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}

export default function FamilyContactDialog({
  open,
  premiseId,
  personRef,
  roomLabel,
  contact,
  defaultTz,
  onClose,
  onSaved,
}: Props) {
  const editing = !!contact;

  const [displayName, setDisplayName] = useState("");
  const [relation, setRelation] = useState("");
  const [avatarAssetId, setAvatarAssetId] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [callableFromRoom, setCallableFromRoom] = useState(true);
  const [canCallRoom, setCanCallRoom] = useState(true);
  const [quietFrom, setQuietFrom] = useState("");
  const [quietTo, setQuietTo] = useState("");
  const [quietTz, setQuietTz] = useState(defaultTz);

  // Consent. `consentDirty` is what keeps an unrelated edit from re-stamping
  // `grantedAt` - see (3) in the header.
  const [consentGiven, setConsentGiven] = useState(false);
  const [capacity, setCapacity] = useState("");
  const [note, setNote] = useState("");
  const [consentDirty, setConsentDirty] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The portal link, shown once after it is minted (PORTAL-2).
  const [invite, setInvite] = useState<{
    url: string;
    expiresAt: string;
    emailed: boolean;
  } | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // The CALL link, shown once after it is minted (VC3-5). A different
  // credential from the one above - see the section header in the form.
  const [callLink, setCallLink] = useState<{
    url: string;
    emailed: boolean;
    rotated: boolean;
  } | null>(null);
  const [callLinkBusy, setCallLinkBusy] = useState(false);
  const [callLinkError, setCallLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    setConsentDirty(false);
    setDisplayName(contact?.displayName ?? "");
    setRelation(contact?.relation ?? "");
    setAvatarAssetId(contact?.avatarAssetId ?? "");
    // Endpoints are encrypted and never read back into a form; blank means
    // "leave what is stored alone".
    setPhone("");
    setEmail("");
    setInvite(null);
    setCallLink(null);
    setCallLinkError(null);
    setInviteError(null);
    setCallableFromRoom(contact ? contact.callableFromRoom : true);
    setCanCallRoom(contact ? contact.canCallRoom : true);
    setQuietFrom(contact?.quietHours?.from ?? "");
    setQuietTo(contact?.quietHours?.to ?? "");
    setQuietTz(contact?.quietHours?.tz ?? defaultTz);
    setConsentGiven(!!contact?.consent);
    setCapacity(contact?.consent?.capacity ?? "");
    setNote(contact?.consent?.note ?? "");
  }, [open, contact, defaultTz]);

  // Un-consented rows are inert, always. Derived rather than written back into
  // state, so that recording consent restores what the operator had already
  // chosen instead of silently un-ticking both directions on them.
  const effectiveCallable = consentGiven && callableFromRoom;
  const effectiveCanCall = consentGiven && canCallRoom;

  const phoneTouched = phone.trim().length > 0;
  const phoneBad = phoneTouched && !isValidE164(phone);
  // On create we need a number; on edit, blank keeps the stored one.
  const phoneMissing = !editing && !phoneTouched;
  const emailTouched = email.trim().length > 0;
  const emailBad = emailTouched && !isValidEmail(email);
  const quietPartial =
    [quietFrom, quietTo].filter((v) => v.trim()).length === 1;
  const consentIncomplete = consentGiven && !capacity;

  // What is already stored, as masks - the values themselves are encrypted and
  // only ever leave the envelope through the audited reveal path.
  const storedPhone = contact?.endpointHints?.phone ?? null;
  const storedEmail = contact?.endpointHints?.email ?? null;

  /**
   * The server replaces the WHOLE endpoint envelope on every write - it cannot
   * merge, because it never reads the old values back into this form. So
   * touching one of the two fields while the other is stored but left blank
   * would quietly erase the other one. That is stated, loudly, instead of being
   * discovered later by a relative who can no longer sign in.
   */
  const endpointsTouched = phoneTouched || emailTouched;
  const losingPhone =
    editing && endpointsTouched && !phoneTouched && !!storedPhone;
  const losingEmail =
    editing && endpointsTouched && !emailTouched && !!storedEmail;

  const valid =
    !!displayName.trim() &&
    !phoneBad &&
    !phoneMissing &&
    !emailBad &&
    !quietPartial &&
    !consentIncomplete;

  /** Withdrawing consent on a live row is a real act; say what it does. */
  const withdrawing = editing && !!contact?.consent && !consentGiven;

  const consentRecord = useMemo(() => {
    if (!contact?.consent) return null;
    const c = contact.consent;
    const when = new Date(c.grantedAt);
    const label =
      CONSENT_CAPACITIES.find((o) => o.value === c.capacity)?.label ??
      c.capacity ??
      "unspecified capacity";
    return `Recorded by ${c.grantedBy} on ${
      Number.isNaN(when.getTime()) ? c.grantedAt : when.toLocaleDateString()
    } - ${label}`;
  }, [contact]);

  const touchConsent =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setConsentDirty(true);
      setter(v);
    };

  async function submit() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);

    const quietHours =
      quietFrom.trim() && quietTo.trim()
        ? {
            from: quietFrom.trim(),
            to: quietTo.trim(),
            tz: quietTz.trim() || defaultTz,
          }
        : null;
    const consent = consentGiven
      ? { capacity: capacity || null, note: note.trim() || null }
      : null;
    const common = {
      displayName: displayName.trim(),
      relation: relation.trim() || null,
      avatarAssetId: avatarAssetId.trim() || null,
      // Consent gates both directions on the wire, not only in the checkboxes.
      callableFromRoom: effectiveCallable,
      canCallRoom: effectiveCanCall,
      quietHours,
    };

    // The complete envelope, always - see the note on `losingPhone` above.
    const endpoints = {
      ...(phoneTouched ? { phoneE164: phone.trim() } : {}),
      ...(emailTouched ? { email: email.trim().toLowerCase() } : {}),
    };

    try {
      if (contact) {
        await foyerApi.updateCallContact(premiseId, contact.id, {
          ...common,
          ...(endpointsTouched ? { endpoints } : {}),
          // Only when the operator actually touched it - otherwise a typo fix
          // would re-date the consent record.
          ...(consentDirty ? { consent } : {}),
        });
        onSaved(
          withdrawing
            ? `Consent withdrawn - ${common.displayName} no longer reaches the room`
            : `${common.displayName} updated`
        );
      } else {
        const input: CallContactInput = {
          scope: "person",
          personRef,
          kind: "family",
          endpoints,
          autoAnswer: false,
          consent,
          ...common,
        };
        await foyerApi.createCallContact(premiseId, input);
        onSaved(
          consentGiven
            ? `${common.displayName} added`
            : `${common.displayName} saved as inactive - consent still missing`
        );
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Mint a portal link for this contact. Staff get the plaintext link back ONCE
   * - it is also mailed when the contact has an email and a mail provider is
   * configured,
   * and `emailed` says which of the two actually happened, so nobody assumes a
   * delivery that never left the building.
   */
  async function sendInvite() {
    if (!contact || inviteBusy) return;
    setInviteBusy(true);
    setInviteError(null);
    try {
      setInvite(await foyerApi.sendFamilyPortalInvite(premiseId, contact.id));
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : String(err));
    } finally {
      setInviteBusy(false);
    }
  }

  /**
   * Mint the CALL link (VC3-5) - the one that lets this relative answer a call.
   * Same "shown once, mailed when we can" contract as the portal link, with one
   * extra thing to say: minting it retires the previous one, so a link already
   * sent to the family stops working. `rotated` says whether that just happened.
   */
  async function sendCallLink() {
    if (!contact || callLinkBusy) return;
    setCallLinkBusy(true);
    setCallLinkError(null);
    try {
      setCallLink(await foyerApi.sendFamilyCallLink(premiseId, contact.id));
    } catch (err) {
      setCallLinkError(err instanceof Error ? err.message : String(err));
    } finally {
      setCallLinkBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="family-contact-dialog"
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        {editing ? "Edit family contact" : "Add family contact"}
        <Typography variant="caption" color="text.secondary" display="block">
          {roomLabel}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            size="small"
            label="Name shown on the TV"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            autoFocus
            required
            helperText="First names only - “Anna”, not “Anna Lundqvist”. This text is not encrypted and shows on a TV in a room that visitors and cleaners walk through."
            inputProps={{ "data-testid": "family-display-name" }}
          />

          <Autocomplete
            freeSolo
            options={RELATION_SUGGESTIONS}
            value={relation}
            onInputChange={(_, v) => setRelation(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Relation (optional)"
                placeholder="dotter"
                helperText="Shown under the name on the TV, so the resident knows who is calling."
                inputProps={{
                  ...params.inputProps,
                  "data-testid": "family-relation",
                }}
              />
            )}
          />

          <Box>
            <AssetField
              label="Photo (optional)"
              value={avatarAssetId}
              onChange={setAvatarAssetId}
              testId="family-avatar"
              preview
            />
          </Box>

          <TextField
            size="small"
            label={
              editing ? "Phone (leave blank to keep)" : "Phone +4671234567"
            }
            placeholder="+4671234567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            required={!editing}
            error={phoneBad}
            helperText={
              phoneBad
                ? "Must be E.164: a leading + and country code, no spaces or dashes - e.g. +46701234567."
                : editing
                  ? `${storedPhone ? `Stored: ${storedPhone}. ` : ""}Encrypted at rest and never read back into this form. Leave blank to keep the stored number.`
                  : "Encrypted at rest, never shown on a TV, and only ever revealed by an explicit, logged action."
            }
            inputProps={{ "data-testid": "family-phone" }}
          />

          <TextField
            size="small"
            label={editing ? "Email (leave blank to keep)" : "Email (optional)"}
            placeholder="anna@example.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            error={emailBad}
            helperText={
              emailBad
                ? "That does not look like an email address."
                : storedEmail
                  ? `Stored: ${storedEmail}. Where both links below are mailed, and how they request a portal login themselves.`
                  : editing
                    ? "Where we can mail the links below. Without it nothing is sent automatically - you hand the link over yourself - and the relative cannot request a portal login on their own."
                    : "Where we can mail their links once the contact is saved. Optional: without it you simply hand the link over yourself."
            }
            inputProps={{ "data-testid": "family-email" }}
          />

          {(losingPhone || losingEmail) && (
            <Alert severity="warning" data-testid="family-endpoint-loss">
              Contact details are saved as one set, so filling in only one of
              them replaces both. Saving now removes the stored{" "}
              {losingPhone ? `number (${storedPhone})` : ""}
              {losingPhone && losingEmail ? " and " : ""}
              {losingEmail ? `email (${storedEmail})` : ""} - re-enter it above
              to keep it.
            </Alert>
          )}

          {editing && (
            <>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Links for the family
                </Typography>
              </Divider>

              <Typography variant="caption" color="text.secondary">
                Two different links, and they are not interchangeable. The{" "}
                <strong>call link</strong> is how this relative answers a call
                on their phone. The <strong>portal link</strong> is how they
                sign in to see and pay for video calling. Both are shown once.
              </Typography>

              {/* VC3-5 - the credential that lets them pick up. */}
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => void sendCallLink()}
                  disabled={callLinkBusy}
                  data-testid="family-call-link"
                >
                  {callLinkBusy ? "Creating link…" : "Send call link"}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  Their personal link to call.nenda.com, where they answer when
                  the room rings and can enable notifications. It does not
                  expire - it stops working when you revoke this contact or the
                  resident is discharged. Creating a new one immediately
                  invalidates the link they already have.
                  {!consentGiven &&
                    " Without a consent record the link opens but nobody can ring anybody."}
                </Typography>
                {callLinkError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {callLinkError}
                  </Alert>
                )}
                {callLink && (
                  <Alert
                    severity={callLink.rotated ? "warning" : "success"}
                    sx={{ mt: 1, wordBreak: "break-all" }}
                    data-testid="family-call-link-result"
                  >
                    {callLink.rotated
                      ? "A previous call link existed and has just stopped working - this new one has to reach them, or they cannot answer. "
                      : ""}
                    {callLink.emailed
                      ? "Mailed to the stored address. Copy it now anyway - it is not shown again:"
                      : "Not mailed (no email on file, or mail is not configured). Copy it now - it is not shown again:"}
                    <br />
                    <strong>{callLink.url}</strong>
                  </Alert>
                )}
              </Box>

              {/* PORTAL-2 - the durable, money-spending surface. */}
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => void sendInvite()}
                  disabled={inviteBusy}
                  data-testid="family-portal-invite"
                >
                  {inviteBusy ? "Creating link…" : "Send family portal link"}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  A single-use link into family.nenda.com, valid 7 days. Mailed
                  automatically when the contact has an email - otherwise read
                  it out or write it down, it is shown once.
                </Typography>
                {inviteError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {inviteError}
                  </Alert>
                )}
                {invite && (
                  <Alert
                    severity="success"
                    sx={{ mt: 1, wordBreak: "break-all" }}
                    data-testid="family-portal-invite-result"
                  >
                    {invite.emailed
                      ? "Link mailed to the stored address. Copy it now anyway - it is not shown again:"
                      : "Not mailed (no email on file, or mail is not configured). Copy it now - it is not shown again:"}
                    <br />
                    <strong>{invite.url}</strong>
                  </Alert>
                )}
              </Box>
            </>
          )}

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              Consent
            </Typography>
          </Divider>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={consentGiven}
                  onChange={(e) => {
                    setConsentDirty(true);
                    setConsentGiven(e.target.checked);
                  }}
                  data-testid="family-consent-given"
                />
              }
              label="Consent has been given for this contact"
            />
            {consentRecord && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                data-testid="family-consent-record"
              >
                {consentRecord}
              </Typography>
            )}
          </Box>

          {consentGiven ? (
            <Stack spacing={2}>
              <FormControl
                size="small"
                fullWidth
                required
                error={consentIncomplete}
              >
                <InputLabel id="family-capacity-label">
                  Given in what capacity
                </InputLabel>
                <Select
                  labelId="family-capacity-label"
                  label="Given in what capacity"
                  value={capacity}
                  onChange={(e) => touchConsent(setCapacity)(e.target.value)}
                  data-testid="family-consent-capacity"
                >
                  {CONSENT_CAPACITIES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {consentIncomplete
                    ? "Required - a consent record that doesn’t say whose consent it was answers nothing."
                    : "If the resident has a god man or förvaltare, their decision is not the resident’s own."}
                </FormHelperText>
              </FormControl>
              <TextField
                size="small"
                label="Note (optional)"
                placeholder="Consent given at the admission meeting 2026-07-20, present: …"
                value={note}
                onChange={(e) => touchConsent(setNote)(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                helperText="How and when consent was obtained. Avoid personal identity numbers - this note is not the medical record."
                inputProps={{ "data-testid": "family-consent-note" }}
              />
            </Stack>
          ) : (
            <Alert severity="warning" data-testid="family-consent-missing">
              {withdrawing
                ? "Withdrawing consent takes this contact off the resident’s TV immediately and stops them ringing the room. The row is kept, inactive, so past calls stay explicable."
                : "Without a consent record this contact is saved as inactive: it will not appear on the resident’s TV and cannot ring the room. Everything else you enter is kept - come back and record consent to activate it."}
            </Alert>
          )}

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              What they may do
            </Typography>
          </Divider>

          <Tooltip
            title={
              consentGiven
                ? ""
                : "Record consent first - these are what actually put a relative in a resident’s room."
            }
          >
            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={effectiveCallable}
                    disabled={!consentGiven}
                    onChange={(e) => setCallableFromRoom(e.target.checked)}
                    inputProps={
                      {
                        "data-testid": "family-callable-from-room",
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label="The resident can call the contact"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={effectiveCanCall}
                    disabled={!consentGiven}
                    onChange={(e) => setCanCallRoom(e.target.checked)}
                    inputProps={
                      {
                        "data-testid": "family-can-call-room",
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label="They can call the resident"
              />
            </Stack>
          </Tooltip>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              size="small"
              type="time"
              label="Quiet hours from"
              value={quietFrom}
              onChange={(e) => setQuietFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={quietPartial}
              inputProps={{ "data-testid": "family-quiet-from" }}
            />
            <TextField
              size="small"
              type="time"
              label="Quiet hours to"
              value={quietTo}
              onChange={(e) => setQuietTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={quietPartial}
              inputProps={{ "data-testid": "family-quiet-to" }}
            />
            <TimezoneSelect
              value={quietTz}
              onChange={(tz) => setQuietTz(tz || defaultTz)}
              testId="family-quiet-tz"
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {quietPartial
              ? "Set both ends of the quiet window, or neither."
              : "Quiet hours only stop this contact from calling the room - the resident can always call out, at any hour."}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={!valid || busy}
          data-testid="family-submit"
        >
          {busy
            ? "Saving…"
            : editing
              ? "Save"
              : consentGiven
                ? "Add contact"
                : "Save as inactive"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
